import { useEffect, useRef, type PointerEvent, type TouchEvent } from 'react'

export function TalkOrb({
  hot = false,
  linking = false,
  dead = false,
  scale = 1,
  label,
  hint,
  visual = false,
  onPointerDown,
  onPointerUp,
  onTouchStart,
  onTouchEnd,
  onPointerCancel,
  onLostPointerCapture,
  onClick,
}: {
  hot?: boolean
  linking?: boolean
  dead?: boolean
  scale?: number
  label: string
  hint?: string
  visual?: boolean
  onPointerDown?: (e: PointerEvent<HTMLButtonElement>) => void
  onPointerUp?: () => void
  onTouchStart?: (e: TouchEvent<HTMLButtonElement>) => void
  onTouchEnd?: (e: TouchEvent<HTMLButtonElement>) => void
  onPointerCancel?: () => void
  onLostPointerCapture?: () => void
  onClick?: () => void
}) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const button = buttonRef.current
    if (!button) return
    const hold = (event: globalThis.TouchEvent) => { event.preventDefault() }
    button.addEventListener('touchstart', hold, { passive: false })
    return () => button.removeEventListener('touchstart', hold)
  }, [])
  const showLink = linking && !hot && !dead
  const pegel = showLink ? (
    <span className="talk-pegel" aria-hidden>
      <span className="talk-pegel-wave" />
    </span>
  ) : null
  const arc = showLink ? (
    <svg className="talk-arc" viewBox="0 0 100 100" aria-hidden>
      <circle className="talk-arc-track" cx="50" cy="50" r="46" />
      <circle className="talk-arc-sweep" cx="50" cy="50" r="46" pathLength="100" />
    </svg>
  ) : null
  const core = (
    <span className="talk-orb-core">
      {hot ? (
        <span className="talk-eq" aria-hidden>
          <i /><i /><i /><i /><i /><i /><i />
        </span>
      ) : (
        <span className="talk-idle-mic" aria-hidden>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M6 11a6 6 0 0 0 12 0" />
            <path d="M12 17v3" />
          </svg>
        </span>
      )}
      {!hot && !showLink && hint ? <em aria-hidden>{hint}</em> : null}
    </span>
  )
  return (
    <div
      className={`talk-orb-field ${hot ? 'hot' : ''} ${showLink ? 'linking' : ''} ${dead ? 'dead' : ''}`}
      style={{ ['--orb-scale' as string]: String(scale) }}
    >
      <div className="talk-halo" />
      <div className="talk-ring r1" />
      <div className="talk-ring r2" />
      <div className="talk-ring r3" />
      <i className="talk-spark" style={{ ['--a' as string]: '20deg' }} />
      <i className="talk-spark" style={{ ['--a' as string]: '140deg' }} />
      <i className="talk-spark" style={{ ['--a' as string]: '250deg' }} />
      {arc}
      {visual ? (
        <span className={`talk-orb ${hot ? 'hot' : ''} ${showLink ? 'linking' : ''} ${dead ? 'dead' : ''}`}>{pegel}{core}</span>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className={`talk-orb ${hot ? 'hot' : ''} ${showLink ? 'linking' : ''} ${dead ? 'dead' : ''}`}
          aria-label={label}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onPointerCancel={onPointerCancel}
          onLostPointerCapture={onLostPointerCapture}
          onClick={onClick}
          onContextMenu={e => e.preventDefault()}
        >
          {pegel}{core}
        </button>
      )}
    </div>
  )
}
